import React, { useState, useEffect } from "react"
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
} from "@mui/material"
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined"
import SectionBox from "../../WorkforceManagement/components/SectionBox"
import ShareIcon from "@mui/icons-material/Share"
import { getAttendanceSheet, updateAttendanceRecordsBulk, shareAttendanceSheetWithAdmin, getRosterDetails } from "../../../utils/RosterAPI"
import { toast } from "react-toastify"
import AddWorkersModal from "./AddWorkersModal"

export default function WeeklyScheduleOverview({ attendanceSheetId, weekStartDate, weekEndDate, rosterId }) {
    const [attendanceSheet, setAttendanceSheet] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [openAddWorkersModal, setOpenAddWorkersModal] = useState(false)
    const [rosterWorkers, setRosterWorkers] = useState([])
    const [selectedWorkers, setSelectedWorkers] = useState([])
    const [loadingRoster, setLoadingRoster] = useState(false)

    // Generate week days from start and end dates
    const generateWeekDays = (startDate, endDate) => {
        if (!startDate || !endDate) return []
        
        const start = new Date(startDate)
        const days = []
        const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
        
        for (let i = 0; i < 7; i++) {
            const currentDate = new Date(start)
            currentDate.setDate(start.getDate() + i)
            const dayName = dayNames[currentDate.getDay()]
            const day = String(currentDate.getDate()).padStart(2, "0")
            const month = String(currentDate.getMonth() + 1).padStart(2, "0")
            
            days.push({
                day: dayName,
                date: `${day}/${month}`,
                fullDate: currentDate.toISOString().split('T')[0], // YYYY-MM-DD format
            })
        }
        
        return days
    }

    const weekDays = generateWeekDays(weekStartDate, weekEndDate)

    // Fetch attendance sheet data
    useEffect(() => {
        if (attendanceSheetId) {
            fetchAttendanceSheet()
        }
    }, [attendanceSheetId])

    const fetchAttendanceSheet = async () => {
        setLoading(true)
        try {
            const response = await getAttendanceSheet(attendanceSheetId)
            if (response) {
                setAttendanceSheet(response)
            }
        } catch (error) {
            console.error("Error fetching attendance sheet:", error)
            toast.error(error?.message || "Failed to fetch attendance sheet")
        } finally {
            setLoading(false)
        }
    }

    // Build a map of worker -> date -> attendance record for easy lookup
    const buildAttendanceMap = () => {
        if (!attendanceSheet?.attendanceRecords) return {}
        
        const map = {}
        attendanceSheet.attendanceRecords.forEach((record) => {
            if (!map[record.id]) {
                map[record.id] = {}
            }
            record.schedule?.forEach((scheduleItem) => {
                map[record.id][scheduleItem.date] = {
                    attendanceType: scheduleItem.attendanceType,
                }
            })
        })
        return map
    }

    const attendanceMap = buildAttendanceMap()
    const allWorkers = attendanceSheet?.attendanceRecords || []

    // Get attendance type for a worker-date combination
    const getAttendanceType = (workerId, date) => {
        return attendanceMap[workerId]?.[date]?.attendanceType || null
    }

    // Check if attendance is marked as present
    const isPresent = (attendanceType) => {
        if (!attendanceType) return false
        const typeUpper = attendanceType.toUpperCase()
        // Consider PRESENT, FULL_DAY, HALF_DAY as "present" for backwards compatibility
        return typeUpper === "PRESENT" || typeUpper === "FULL_DAY" || typeUpper === "FULLDAY" || typeUpper === "HALF_DAY" || typeUpper === "HALFDAY"
    }

    const workers = allWorkers

    // Handle cell click - direct toggle and auto-save
    const handleCellClick = async (workerId, date) => {
        const sheetId = attendanceSheet?.id || attendanceSheetId
        if (!sheetId) {
            toast.error("Attendance sheet ID is missing")
            return
        }

        const currentType = getAttendanceType(workerId, date)
        const currentlyPresent = isPresent(currentType)
        // Use FULL_DAY for present, ABSENT for not present (API expects FULL_DAY, HALF_DAY, or ABSENT)
        const newAttendanceType = currentlyPresent ? "ABSENT" : "FULL_DAY"
        
        // Optimistically update the local attendance sheet state
        setAttendanceSheet((prev) => {
            if (!prev) return prev
            
            const updatedRecords = prev.attendanceRecords?.map((record) => {
                if (record.id === workerId) {
                    const updatedSchedule = record.schedule?.map((scheduleItem) => {
                        if (scheduleItem.date === date) {
                            return { ...scheduleItem, attendanceType: newAttendanceType }
                        }
                        return scheduleItem
                    }) || []
                    
                    // If the date doesn't exist in schedule, add it
                    const dateExists = record.schedule?.some(s => s.date === date)
                    if (!dateExists) {
                        updatedSchedule.push({ date, attendanceType: newAttendanceType })
                    }
                    
                    return { ...record, schedule: updatedSchedule }
                }
                return record
            }) || []
            
            return { ...prev, attendanceRecords: updatedRecords }
        })

        // Auto-save to API
        try {
            const attendanceRecords = [{
                workerId: typeof workerId === 'string' ? parseInt(workerId, 10) : workerId,
                date: date,
                attendanceType: newAttendanceType,
                remarks: null,
            }]

            await updateAttendanceRecordsBulk(sheetId, attendanceRecords)
        } catch (error) {
            console.error("Error saving attendance:", error)
            toast.error("Failed to save attendance")
            // Revert by refetching on error
            await fetchAttendanceSheet()
        }
    }

    // Handle open add workers modal
    const handleOpenAddWorkers = async () => {
        if (!rosterId) {
            toast.error("Roster ID is missing")
            return
        }

        setLoadingRoster(true)
        try {
            const response = await getRosterDetails(rosterId)
            if (response?.workers) {
                // Filter out workers that are already in the attendance sheet
                const existingWorkerIds = new Set(workers.map(w => w.id))
                const availableWorkers = response.workers.filter(w => !existingWorkerIds.has(w.id))
                setRosterWorkers(availableWorkers)
                setSelectedWorkers([])
                setOpenAddWorkersModal(true)
            }
        } catch (error) {
            console.error("Error fetching roster workers:", error)
            toast.error(error?.message || "Failed to fetch roster workers")
        } finally {
            setLoadingRoster(false)
        }
    }

    // Handle add workers to attendance sheet
    const handleAddWorkers = async () => {
        if (selectedWorkers.length === 0) {
            toast.error("Please select at least one worker")
            return
        }

        const sheetId = attendanceSheet?.id || attendanceSheetId
        if (!sheetId) {
            toast.error("Attendance sheet ID is missing")
            return
        }

        // Create attendance records for selected workers for all days in the week
        const attendanceRecords = []
        selectedWorkers.forEach((worker) => {
            weekDays.forEach((day) => {
                attendanceRecords.push({
                    workerId: worker.id,
                    date: day.fullDate,
                    attendanceType: null, // Start with no attendance type - user will set it
                    remarks: null,
                })
            })
        })

        setSaving(true)
        try {
            const response = await updateAttendanceRecordsBulk(sheetId, attendanceRecords)
            if (response) {
                toast.success(`Successfully added ${selectedWorkers.length} worker(s) to attendance sheet`)
                setOpenAddWorkersModal(false)
                setSelectedWorkers([])
                await fetchAttendanceSheet() // Refresh to show new workers
            }
        } catch (error) {
            console.error("Error adding workers:", error)
            toast.error(error?.message || "Failed to add workers")
        } finally {
            setSaving(false)
        }
    }

    // Calculate totals (8 hours per present day)
    const calculateTotals = () => {
        let totalHours = 0

        workers.forEach((worker) => {
            weekDays.forEach((day) => {
                const attendanceType = getAttendanceType(worker.id, day.fullDate)
                if (isPresent(attendanceType)) {
                    totalHours += 8
                }
            })
        })

        return { totalHours }
    }

    const { totalHours } = calculateTotals()

    // Calculate total hours for a worker (8 hours per present day)
    const calculateWorkerHours = (workerId) => {
        let hours = 0
        weekDays.forEach((day) => {
            const attendanceType = getAttendanceType(workerId, day.fullDate)
            if (isPresent(attendanceType)) {
                hours += 8
            }
        })
        return hours
    }

    // Share with admin
    const handleShare = async () => {
        const sheetId = attendanceSheet?.id || attendanceSheetId
        if (!sheetId) {
            toast.error("Attendance sheet ID is missing")
            return
        }

        if (!window.confirm("Are you sure you want to share this attendance sheet with Reno? Once shared, you won't be able to make changes.")) {
            return
        }

        try {
            const response = await shareAttendanceSheetWithAdmin(sheetId)
            if (response) {
                toast.success("Attendance sheet shared successfully")
                await fetchAttendanceSheet() // Refresh to get updated data
            }
        } catch (error) {
            console.error("Error sharing attendance sheet:", error)
            toast.error(error?.message || "Failed to share attendance sheet")
        }
    }

    if (loading) {
        return (
            <SectionBox
                icon={<PersonAddAltOutlinedIcon sx={{ fontSize: 22, color: "#101828" }} />}
                title="Weekly Schedule Overview"
                titleSize="24px"
            >
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            </SectionBox>
        )
    }

    if (!attendanceSheet) {
        return (
            <SectionBox
                icon={<PersonAddAltOutlinedIcon sx={{ fontSize: 22, color: "#101828" }} />}
                title="Weekly Schedule Overview"
                titleSize="24px"
            >
                <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                        No attendance sheet data available
                    </Typography>
                </Box>
            </SectionBox>
        )
    }

    return (
        <SectionBox
            icon={<PersonAddAltOutlinedIcon sx={{ fontSize: 22, color: "#101828" }} />}
            title="Weekly Schedule Overview"
            titleSize="24px"
        >
            {/* Table */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    border: "1px solid #E4E7EC",
                    borderRadius: "8px",
                    overflow: "hidden",
                }}
            >
                <Table sx={{ tableLayout: "fixed" }}>
                    {/* Table Head */}
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                        <TableRow>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    color: "#101828",
                                    fontSize: "13px",
                                    borderBottom: "1px solid #E4E7EC",
                                    padding: "6px 8px",
                                    width: "140px",
                                }}
                            >
                                Worker
                            </TableCell>
                            {weekDays.map((day, index) => (
                                <TableCell
                                    key={day.day}
                                    align="center"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#344054",
                                        fontSize: "12px",
                                        borderBottom: "1px solid #E4E7EC",
                                        borderLeft: "1px solid #E4E7EC",
                                        borderRight: index === weekDays.length - 1 ? "1px solid #E4E7EC" : "none",
                                        padding: "4px 2px",
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: "11px" }}>{day.day}</Typography>
                                        <Typography sx={{ fontSize: "10px", color: "#667085" }}>
                                            {day.date}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            ))}
                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: 600,
                                    color: "#101828",
                                    fontSize: "11px",
                                    borderBottom: "1px solid #E4E7EC",
                                    padding: "6px 4px",
                                    width: "60px",
                                }}
                            >
                                Hours
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {/* Table Body */}
                    <TableBody>
                        {workers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "#667085" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                        <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                            No workers added to this attendance sheet yet
                                        </Typography>
                                            <Button
                                                variant="contained"
                                                startIcon={<PersonAddAltOutlinedIcon />}
                                                onClick={handleOpenAddWorkers}
                                                sx={{
                                                    backgroundColor: "#4D6BF6",
                                                    textTransform: "none",
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    borderRadius: "8px",
                                                    "&:hover": { backgroundColor: "#3D5BD6" },
                                                }}
                                            >
                                                Add Workers
                                            </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            workers.map((worker) => (
                                <TableRow key={worker.id}>
                                    <TableCell
                                        sx={{
                                            fontSize: "12px",
                                            color: "#101828",
                                            fontWeight: 600,
                                            borderBottom: "1px solid #E4E7EC",
                                            padding: "4px 8px",
                                        }}
                                    >
                                        {`${worker.firstName} ${worker.lastName}`}
                                        <Typography sx={{ color: "#667085", fontSize: "10px" }}>
                                            {worker.speciality}
                                        </Typography>
                                    </TableCell>

                                    {weekDays.map((day, dayIndex) => {
                                        const attendanceType = getAttendanceType(worker.id, day.fullDate)
                                        const workerIsPresent = isPresent(attendanceType)

                                        return (
                                            <TableCell
                                                key={day.day}
                                                align="center"
                                                sx={{
                                                    borderBottom: "1px solid #E4E7EC",
                                                    borderLeft: "1px solid #E4E7EC",
                                                    borderRight: dayIndex === weekDays.length - 1 ? "1px solid #E4E7EC" : "none",
                                                    color: "#101828",
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    padding: "2px",
                                                }}
                                            >
                                                        <Box
                                                    onClick={() => handleCellClick(worker.id, day.fullDate)}
                                                            sx={{
                                                                cursor: "pointer",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                        width: "100%",
                                                        height: "20px",
                                                        borderRadius: "2px",
                                                        transition: "all 0.15s ease",
                                                                "&:hover": {
                                                                    backgroundColor: "#F4F3FF",
                                                                },
                                                            }}
                                                        >
                                                    <Typography 
                                                            sx={{
                                                            fontSize: "14px", 
                                                            fontWeight: 600,
                                                            color: workerIsPresent ? "#101828" : "#D0D5DD",
                                                            lineHeight: 1,
                                                            }}
                                                        >
                                                        {workerIsPresent ? "+" : "−"}
                                                        </Typography>
                                                </Box>
                                            </TableCell>
                                        )
                                    })}

                                    <TableCell
                                        align="center"
                                        sx={{
                                            borderBottom: "1px solid #E4E7EC",
                                            fontWeight: 600,
                                            color: "#101828",
                                            fontSize: "11px",
                                            padding: "4px",
                                        }}
                                    >
                                        {calculateWorkerHours(worker.id)}h
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Footer summary */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: "0.8rem",
                }}
            >
                <Typography sx={{ color: "#667085", fontSize: "13px" }}>
                    Total Hours: {totalHours}h
                </Typography>
            </Box>

            {/* Action Buttons */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "0.8rem",
                        mt: "1.5rem",
                        pt: "1rem",
                        borderTop: "1px solid #E4E7EC",
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<PersonAddAltOutlinedIcon />}
                        onClick={handleOpenAddWorkers}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#101828",
                            borderColor: "#E4E7EC",
                            borderRadius: "8px",
                            "&:hover": { borderColor: "#D0D5DD" },
                        }}
                    >
                        Add Workers
                    </Button>

                    <Box sx={{ display: "flex", gap: "0.8rem" }}>
                        <Button
                            variant="contained"
                            startIcon={<ShareIcon />}
                            onClick={handleShare}
                            sx={{
                            backgroundColor: "#4D6BF6",
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                borderRadius: "8px",
                            "&:hover": { backgroundColor: "#3D5BD6" },
                            }}
                        >
                        Share with Admin
                        </Button>
                    </Box>
                </Box>

            {/* Add Workers Modal */}
            <AddWorkersModal
                open={openAddWorkersModal}
                onClose={() => {
                    setOpenAddWorkersModal(false)
                    setSelectedWorkers([])
                }}
                workers={rosterWorkers}
                selectedWorkers={selectedWorkers}
                onWorkersChange={setSelectedWorkers}
                onAdd={handleAddWorkers}
                loading={saving || loadingRoster}
            />
        </SectionBox>
    )
}
